import { Create, Delete, Lock } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { ErrorModal } from "../../../../../../components/modals/error-modal";
import MesfModal from "../../../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../../../components/shared/buttons/button-with-loading";
import { useTrendingContextV2 } from "../../context/TrendingContextV2";
import { View } from "../../models/TrendingModelsV2";
import { SaveUpdateDeleteViewModalV2 } from "./SaveUpdateDeleteViewModalV2";

interface Props {
	open: boolean;
	handleClose: () => void;
}

export const LoadViewModalV2 = ({ open, handleClose }: Props) => {
	const queryClient = useQueryClient();

	const {
		state: { views, viewSelected },
		actions: { setViewSelected },
	} = useTrendingContextV2();
	const [viewModalMode, setViewModalMode] = useState<
		"create" | "update" | "delete" | null
	>(null);
	const [viewForModal, setViewForModal] = useState<View | null>(null);

	const [optionSelected, setOptionSelected] = useState<View | null>(null);

	const [allViewsChecked, setAllViewsChecked] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const [error, setError] = useState("");

	const handleSelect = async () => {
		setIsLoading(true);
		if (optionSelected) {
			setViewSelected(optionSelected);
			// Invalidate the viewTags query to trigger a refetch with the new viewId
			await queryClient.invalidateQueries({
				queryKey: ["viewTags-v2", optionSelected.ViewId],
			});
		}
		setIsLoading(false);
		handleClose();
	};

	const handleDeleteView = async (view: View, e: any) => {
		e.stopPropagation();
		if (view.ViewId === viewSelected?.ViewId) {
			setError("Cannot delete current view");
		} else {
			setViewForModal(view);
			setViewModalMode("delete");
		}
	};

	const handleEditView = async (view: View, e: any) => {
		e.stopPropagation();
		setViewForModal(view);
		setViewModalMode("update");
	};

	const handleAllViewsCheckbox = () => {
		setAllViewsChecked((prev) => !prev);
	};

	const publicViews: View[] = useMemo(() => {
		return views.filter((v) => v.IsPublic === true);
	}, [views]);

	const displayedViews = allViewsChecked ? views : publicViews;

	return (
		<div>
			<MesfModal
				open={open}
				handleClose={handleClose}
				maxWidth={"xl"}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				title="Views"
			>
				<MesfModal.Content dividers>
					<Grid2 container>
						{views.length ? (
							<>
								<Grid2 size={12}>
									<Box
										sx={{
											paddingBottom: "1rem",
											userSelect: "none",
											paddingLeft: "1rem",
											"& > label": {
												display: "flex",
												alignItems: "center",
												gap: "15px",
											},
											'& input[type="checkbox"]': {
												zoom: 1.5,
											},
										}}
									>
										<label htmlFor="">
											<input
												type="checkbox"
												checked={allViewsChecked}
												onChange={handleAllViewsCheckbox}
											/>
											<Typography>All Views</Typography>
										</label>
									</Box>
								</Grid2>
								<Grid2 size={12}>
									<Autocomplete
										size="small"
										id="list-of-views"
										noOptionsText={
											allViewsChecked
												? "No views detected, please create a new view"
												: "No public views detected, please create a new view"
										}
										options={displayedViews}
										getOptionLabel={(option) => option.ViewName}
										onChange={(ev, val) => setOptionSelected(val)}
										defaultValue={optionSelected || viewSelected}
										style={{ width: "100%" }}
										renderOption={(props, option) => (
											<Box {...props} component="li">
												<Box
													sx={{
														display: "flex",
														justifyContent: "flex-start",
														alignItems: "center",
														width: "100%",
													}}
												>
													<Box flexGrow={1}>{option.ViewName}</Box>
													<Box
														sx={{
															display: "flex",
															justifyContent: "flex-end",
															alignItems: "center",
															flex: 1,
														}}
													>
														{option.IsPublic ? (
															""
														) : (
															<>
																<Tooltip title={"Private"} arrow>
																	<Lock
																		fontSize="small"
																		style={{
																			color: "black",
																			height: "18px",
																			width: "18px",
																			cursor: "auto",
																		}}
																	/>
																</Tooltip>
															</>
														)}
														<IconButton
															onClick={(
																e: React.MouseEvent<
																	HTMLButtonElement,
																	MouseEvent
																>,
															) => handleEditView(option, e)}
														>
															<Create
																fontSize="small"
																color="primary"
																style={{
																	height: "18px",
																	width: "18px",
																	cursor: "pointer",
																}}
															/>
														</IconButton>
														<IconButton
															onClick={(
																e: React.MouseEvent<
																	HTMLButtonElement,
																	MouseEvent
																>,
															) => handleDeleteView(option, e)}
														>
															<Delete
																fontSize="small"
																color="secondary"
																style={{
																	height: "18px",
																	width: "18px",
																	cursor: "pointer",
																}}
															/>
														</IconButton>
													</Box>
												</Box>
											</Box>
										)}
										renderInput={(params) => (
											<TextField {...params} label="Views" variant="outlined" />
										)}
									/>
								</Grid2>
							</>
						) : (
							<Typography>No views detected, please add one</Typography>
						)}
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions>
					<div style={{ paddingTop: "1rem" }}>
						<Button
							variant="outlined"
							style={{ marginRight: "1rem" }}
							color="secondary"
							onClick={handleClose}
						>
							Cancel
						</Button>
						{views.length ? (
							<ButtonWithLoading
								onClick={handleSelect}
								variant={"contained"}
								color={"primary"}
								isLoading={isLoading}
								style={{ marginRight: "1rem" }}
								disabled={!optionSelected}
							>
								Apply
							</ButtonWithLoading>
						) : (
							<Button
								variant="contained"
								style={{ marginRight: "1rem" }}
								color="primary"
								onClick={handleClose}
							>
								Ok
							</Button>
						)}
					</div>
				</MesfModal.Actions>
			</MesfModal>
			{/* View Management Modal (Update/Delete) */}
			{viewModalMode && (
				<SaveUpdateDeleteViewModalV2
					open={true}
					mode={viewModalMode}
					view={viewForModal}
					handleClose={async (shouldUpdate: boolean) => {
						setViewModalMode(null);
						setViewForModal(null);
						if (shouldUpdate) {
							await queryClient.invalidateQueries({ queryKey: ["views-v2"] });
						}
					}}
				/>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</div>
	);
};
